import React from 'react';
import { Box, Typography } from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import { colors, typography } from '../../../shared/styles/theme';

const STEP_LABELS_UNSIGNED = [
  'Upload Contract',
  'Clause Extraction',
  'Risk Assessment',
  'Summary',
] as const;

const STEP_LABELS_SIGNED = [
  'Upload Contract',
  'Clause Extraction',
  'Risk Assessment',
] as const;

const TEAL = colors.primaryLight;
const CONNECTOR_DONE = TEAL;
const CONNECTOR_TODO = colors.border.light;
const INACTIVE_CIRCLE_BG = '#e8ebeb';
const INACTIVE_NUMBER = colors.text.secondary;

export type ContractReviewProgressActiveStep = 1 | 2 | 3 | 4;

export interface ContractReviewProgressStepperProps {
  activeStep: ContractReviewProgressActiveStep;
  isSigned?: boolean;
}

const CIRCLE_SIZE = 28;

const ContractReviewProgressStepper: React.FC<ContractReviewProgressStepperProps> = ({ activeStep, isSigned = false }) => {
  const STEP_LABELS = isSigned ? STEP_LABELS_SIGNED : STEP_LABELS_UNSIGNED;
  return (
    <Box
      sx={{
        width: '100%',
        mb: 3,
        overflowX: 'auto',
        py: 0.5,
        '&::-webkit-scrollbar': { height: 6 },
        '&::-webkit-scrollbar-thumb': {
          backgroundColor: colors.border.medium,
          borderRadius: 3,
        },
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          minWidth: { xs: 560, sm: '100%' },
        }}
      >
        {STEP_LABELS.map((label, index) => {
          const stepNum = (index + 1) as ContractReviewProgressActiveStep;
          const isComplete = stepNum < activeStep;
          const isActive = stepNum === activeStep;
          const isUpcoming = stepNum > activeStep;

          const showCheck = isComplete;
          const circleFilled = isComplete || isActive;
          const circleBg = circleFilled ? TEAL : INACTIVE_CIRCLE_BG;
          const labelColor = isUpcoming ? colors.text.disabled : colors.text.primary;

          return (
            <React.Fragment key={label}>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.25,
                  flexShrink: 0,
                }}
              >
                <Box
                  sx={{
                    width: CIRCLE_SIZE,
                    height: CIRCLE_SIZE,
                    borderRadius: '50%',
                    backgroundColor: circleBg,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                  }}
                >
                  {showCheck ? (
                    <CheckIcon sx={{ fontSize: 18, color: colors.text.light }} />
                  ) : (
                    <Typography
                      component="span"
                      sx={{
                        fontSize: typography.fontSize.sm,
                        fontWeight: typography.fontWeight.semibold,
                        color: circleFilled ? colors.text.light : INACTIVE_NUMBER,
                        lineHeight: 1,
                      }}
                    >
                      {stepNum}
                    </Typography>
                  )}
                </Box>
                <Typography
                  sx={{
                    fontFamily: typography.fontFamily.primary,
                    fontSize: typography.fontSize.sm,
                    fontWeight: isActive ? typography.fontWeight.semibold : typography.fontWeight.medium,
                    color: labelColor,
                    whiteSpace: 'nowrap',
                  }}
                >
                  {label}
                </Typography>
              </Box>
              {index < STEP_LABELS.length - 1 && (
                <Box
                  sx={{
                    flex: 1,
                    height: 2,
                    minWidth: { xs: 16, sm: 24 },
                    mx: { xs: 0.75, sm: 1.5 },
                    alignSelf: 'center',
                    backgroundColor: stepNum < activeStep ? CONNECTOR_DONE : CONNECTOR_TODO,
                    borderRadius: 1,
                    flexShrink: 1,
                  }}
                />
              )}
            </React.Fragment>
          );
        })}
      </Box>
    </Box>
  );
};

export default ContractReviewProgressStepper;
